import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18next from 'i18next';
import { API_BASE_URL } from '../../config/api';
import { LanguageCode } from '../../helper-types/general/consts/language-code';

class HttpClient {
    private readonly client: AxiosInstance;

    constructor(baseURL: string) {
        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor for Authorization
        this.client.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('bearerToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Request interceptor for language
        this.client.interceptors.request.use(
            (config) => {
                const language = i18next.language || LanguageCode.EN;
                config.headers['Accept-Language'] = language;
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor for 401 handling — a 401 means the session itself is
        // invalid (expired/invalidated token), so force a re-login. A 403 means the
        // opposite: the session IS valid, the caller is just not allowed to do this one
        // thing (e.g. an admin without AccessUsersTab hitting a user-management
        // endpoint) — logging the user out for that would be wrong, so 403s are left for
        // the caller to handle (show a permission-denied message, etc.).
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                const status = error.response?.status;
                const requestUrl = error.config?.url || '';
                const isLoginRequest = requestUrl.includes('log-in') || requestUrl.includes('login');

                if (status === 401 && !isLoginRequest) {
                    localStorage.removeItem('bearerToken');
                    localStorage.removeItem('tenantNameAr');
                    localStorage.removeItem('tenantNameEn');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );

        // Response interceptor for error formatting
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response) {
                    throw new Error(
                        `HTTP error! Status: ${error.response.status}, Message: ${error.response.data?.message || 'Unknown error'}`
                    );
                }
                throw new Error('Network error or server is unreachable');
            }
        );
    }

    async getFromJsonAsync<TResponse>(url: string): Promise<TResponse> {
        const response: AxiosResponse<TResponse> = await this.client.get(url);
        return this.handleResponse(response);
    }

    async getAsync<TResponse>(url: string): Promise<AxiosResponse<TResponse>> {
        return await this.client.get<TResponse>(url);
    }

    async getBlobAsync(url: string): Promise<Blob> {
        const response = await this.client.get(url, { responseType: 'blob' });
        return response.data;
    }

    async postAsJsonAsync<TRequest, TResponse>(
        url: string,
        data: TRequest,
        config?: AxiosRequestConfig
    ): Promise<TResponse> {
        const response: AxiosResponse<TResponse> = await this.client.post(url, data, config);
        return this.handleResponse(response);
    }

    async postFormAsync<TResponse>(
        url: string,
        formData: FormData,
        onProgress?: (percent: number) => void
    ): Promise<TResponse> {
        const response: AxiosResponse<TResponse> = await this.client.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onProgress
                ? (event) => {
                    if (event.total) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                }
                : undefined,
        });
        return this.handleResponse(response);
    }

    async putAsJsonAsync<TRequest, TResponse>(
        url: string,
        data: TRequest
    ): Promise<TResponse> {
        const response: AxiosResponse<TResponse> = await this.client.put(url, data);
        return this.handleResponse(response);
    }

    async deleteFromJsonAsync<TResponse>(url: string): Promise<TResponse> {
        const response: AxiosResponse<TResponse> = await this.client.delete(url);
        return this.handleResponse(response);
    }

    private handleResponse<TResponse>(response: AxiosResponse<TResponse>): TResponse {
        return response.data;
    }
}

// Export a singleton instance
const HTTP_CLIENT = new HttpClient(`${API_BASE_URL}/api`);

export default HTTP_CLIENT;